import React, { useState, useEffect, useCallback } from 'react';
import MobileFilterCollapse from '../../components/common/form/MobileFilterCollapse';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, Paper, Typography, Chip, TextField, MenuItem,
  Grid, Alert, InputAdornment, IconButton, Stack, Menu, Tooltip,
} from '@mui/material';
import { DataGrid } from '../../components/common/datagrid/ResponsiveDataGrid';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import * as shipmentMgmtApi from '../../lib/api/shipmentMgmt';
import { getComCodes } from '../../lib/api/commonCode';

// 완품검사 합격으로 생긴 "출하후보"를 보여주는 화면.
// 지금까지 DB/API 에만 있고 화면이 없어 담당자가 무엇을 내보내야 하는지 볼 곳이 없었다.
// 실제 출고 처리(즉시 출고·창고 입고)는 생산계획 상세의 완료 탭에서 하므로,
// 여기서는 그 화면으로 보내는 링크 역할과 "며칠 미루기" 만 담당한다.

// 상태 옵션은 공통코드(MANU/SHIPMENT_CANDIDATE_STATUS)에서 읽는다.
// PENDING 미출고 / REGISTERED 출고등록 / COMPLETED 출고완료 / CANCELLED 취소 네 가지가
// 등록돼 있고, 목록 쿼리도 이 코드를 조인해 statusName 을 만든다. 화면에 목록을 박아두면
// 코드가 늘었을 때 여기만 빠진다.
const isPendingStatus = (status) => (status || '').toUpperCase().startsWith('PENDING');

const formatDateTime = (value) => {
  if (!value) return '-';
  return String(value).replace('T', ' ').substring(0, 16);
};

const ShipmentCandidateListContainer = () => {
  const navigate = useNavigate();

  // 알림에서 넘어온 "이 건을 강조해라" 지시. 목록 검색 조건(filters)과는 성격이 달라
  // 별도로 다룬다. (ShipmentMgmtListContainer 의 shipmentId 처리와 같은 관례)
  const [searchParams] = useSearchParams();
  const candidateIdParam = searchParams.get('candidateId');

  // 데이터
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 필터
  //
  // 처음에는 아직 안 나간 것만 보여 준다. 화면 이름이 "출하 대기 목록" 인데 이미 나간 건까지
  // 섞여 나오면 무엇이 남았는지 알 수가 없다. 완료된 것을 보려면 상태를 바꾸면 된다.
  const [filters, setFilters] = useState({
    status: 'PENDING',
    keyword: '',
    startDate: '',
    endDate: '',
  });
  // 상태 select 옵션. 공통코드에서 받아온다.
  const [statusOptions, setStatusOptions] = useState([]);

  // 페이징
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });

  // 상태 옵션을 공통코드에서 한 번만 받아온다.
  useEffect(() => {
    let alive = true;
    getComCodes({ blcCd: 'MANU', mlcCd: 'SHIPMENT_CANDIDATE_STATUS', useYn: 'Y' })
      .then((res) => {
        if (!alive) return;
        const list = res.data?.list || [];
        setStatusOptions(
          list
            .slice()
            .sort((a, b) => Number(a.step || 0) - Number(b.step || 0))
            .map((c) => ({ status: c.slcCd, statusName: c.itemNm || c.slcCd })),
        );
      })
      .catch(() => {
        // 코드 조회가 실패해도 목록은 봐야 한다. 상태 필터만 비워둔다.
      });
    return () => { alive = false; };
  }, []);

  // 미루기 메뉴(1/3/7일)
  const [snoozeAnchorEl, setSnoozeAnchorEl] = useState(null);
  const [snoozeTargetId, setSnoozeTargetId] = useState(null);
  const [snoozing, setSnoozing] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: paginationModel.page + 1,
        size: paginationModel.pageSize,
      };
      if (filters.status) params.status = filters.status;
      if (filters.keyword) params.keyword = filters.keyword;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const res = await shipmentMgmtApi.getShipmentCandidates(params);
      if (res.data?.success) {
        const data = res.data.data;
        const content = data.content || [];
        setRows(content.map((item) => ({ ...item, id: item.candidateId })));
        setTotalCount(data.totalElements || 0);
      }
    } catch (err) {
      console.error('출하후보 목록 조회 에러:', err);
      setError('출하후보 목록을 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  }, [paginationModel, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleClearFilters = () => {
    setFilters({ status: '', keyword: '', startDate: '', endDate: '' });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // 출고 처리 화면(생산계획 상세 > 완료 탭)으로 이동한다.
  // projectId/planId 가 없으면 갈 곳이 없어 버튼을 비활성화한다(컬럼 정의에서 처리).
  const handleGoShipment = (row) => {
    navigate(`/manufacturingmng/${row.projectId}/plans/${row.planId}?tab=2`);
  };

  const openSnoozeMenu = (event, candidateId) => {
    setSnoozeAnchorEl(event.currentTarget);
    setSnoozeTargetId(candidateId);
  };

  const closeSnoozeMenu = () => {
    setSnoozeAnchorEl(null);
    setSnoozeTargetId(null);
  };

  // 리마인드를 지정한 일수만큼 멈춘다. 읽음과 달리 기한이 지나면 다시 알림이 온다.
  const handleSnooze = async (days) => {
    const candidateId = snoozeTargetId;
    closeSnoozeMenu();
    if (!candidateId) return;
    setSnoozing(true);
    try {
      await shipmentMgmtApi.snoozeShipmentCandidate(candidateId, days);
      fetchData();
    } catch (err) {
      const message = err?.response?.data?.message || err?.message;
      setError(message || '미루기 처리에 실패했습니다.');
    } finally {
      setSnoozing(false);
    }
  };

  const columns = [
    {
      field: 'planNumber',
      headerName: '계획번호',
      width: 140,
    },
    {
      field: 'productName',
      headerName: '제품',
      flex: 1,
      minWidth: 160,
      // compact 밀도라 행 높이가 낮다. 두 줄로 쌓으면 글자가 서로 겹친다.
      // 제품코드는 한 줄 안에 작게 붙인다.
      renderCell: (params) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" noWrap title={params.value || ''}>
            {params.value || '-'}
            {params.row.productCode && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                {params.row.productCode}
              </Typography>
            )}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'customerName',
      headerName: '고객사',
      width: 130,
    },
    {
      field: 'candidateQty',
      headerName: '후보수량',
      width: 100,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography variant="body2">{(params.value ?? 0).toLocaleString()}</Typography>
      ),
    },
    {
      field: 'registeredQty',
      headerName: '등록수량',
      width: 100,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography variant="body2">{(params.value ?? 0).toLocaleString()}</Typography>
      ),
    },
    {
      field: 'remainingQty',
      headerName: '잔여수량',
      width: 100,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">{(params.value ?? 0).toLocaleString()}</Typography>
      ),
    },
    {
      field: 'phaseCompletedAt',
      headerName: '완료시각',
      width: 150,
      renderCell: (params) => formatDateTime(params.value),
    },
    {
      field: 'workerNm',
      headerName: '담당자',
      width: 100,
    },
    {
      field: 'statusName',
      headerName: '상태',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const chip = (
          <Chip
            label={params.value || params.row.status}
            size="small"
            color={isPendingStatus(params.row.status) ? 'warning' : 'default'}
          />
        );
        // 무시된 건은 사유를 툴팁으로 보여준다.
        if (params.row.ignoreYn === 'Y') {
          return (
            <Tooltip title={params.row.ignoreReason ? `무시됨: ${params.row.ignoreReason}` : '무시된 항목입니다'}>
              <span>{chip}</span>
            </Tooltip>
          );
        }
        return chip;
      },
    },
    {
      field: 'actions',
      headerName: '작업',
      // 가로 스크롤이 생겨도 버튼이 보여야 한다. 오른쪽에 고정한다.
      width: 210,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const row = params.row;
        const canShip = Boolean(row.projectId && row.planId);
        return (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title={canShip ? '' : '계획 정보가 없어 이동할 수 없습니다.'}>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!canShip}
                  onClick={(e) => { e.stopPropagation(); handleGoShipment(row); }}
                >
                  출고하기
                </Button>
              </span>
            </Tooltip>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={(e) => { e.stopPropagation(); openSnoozeMenu(e, row.candidateId); }}
            >
              미루기
            </Button>
          </Stack>
        );
      },
    },
  ];

  return (
    <Box>
      {/* 화면 제목. 이 화면은 메뉴로 바로 들어올 수도 있고 알림·출고관리에서 넘어오기도 해서,
          지금 어디에 있는지 알려주는 머리말이 필요하다. */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">출하 대기 목록</Typography>
        <Typography variant="body2" color="text.secondary">
          완품검사를 통과해 출고할 수 있게 된 건들입니다. 출고하기를 누르면 그 계획의 완료 탭으로 이동합니다.
        </Typography>
      </Box>

      {/* 필터 */}
      <MobileFilterCollapse summary={[filters.startDate && `${filters.startDate} ~ ${filters.endDate || ''}`, filters.keyword && `"${filters.keyword}"`].filter(Boolean).join(' · ')}>
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              select
              label="상태"
              size="small"
              fullWidth
              value={filters.status}
              onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
            >
              <MenuItem value="">전체</MenuItem>
              {statusOptions.map((opt) => (
                <MenuItem key={opt.status} value={opt.status}>{opt.statusName}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              label="시작일"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.startDate}
              onChange={(e) => setFilters((prev) => ({ ...prev, startDate: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              label="종료일"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.endDate}
              onChange={(e) => setFilters((prev) => ({ ...prev, endDate: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="검색"
              size="small"
              fullWidth
              placeholder="계획번호, 제품명, 고객사..."
              value={filters.keyword}
              onChange={(e) => setFilters((prev) => ({ ...prev, keyword: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleSearch}>
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={1}>
            <Button size="small" onClick={handleClearFilters} startIcon={<ClearIcon />}>
              초기화
            </Button>
          </Grid>
        </Grid>
      </Paper>
      </MobileFilterCollapse>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {/* 목록 */}
      <Paper elevation={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShippingIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight="bold">출하 대기 목록</Typography>
            <Chip label={`${totalCount}건`} size="small" />
          </Box>
        </Box>
        <DataGrid
          rows={rows}
          columns={columns}
          rowCount={totalCount}
          loading={loading}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          autoHeight
          density="compact"
          disableRowSelectionOnClick
          // 알림에서 넘어온 건은 노란 배경으로, 무시된 건은 회색으로 흐리게 보여준다.
          // candidateIdParam 이 바뀌면(같은 경로에서 다른 알림을 눌러도) 다시 반응해야 하므로
          // 렌더 시점의 값을 그대로 참조한다.
          getRowClassName={(params) => {
            const classes = [];
            if (params.row.ignoreYn === 'Y') classes.push('ignored-row');
            if (candidateIdParam && String(params.row.candidateId) === candidateIdParam) {
              classes.push('highlighted-row');
            }
            return classes.join(' ');
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
            '& .ignored-row': { backgroundColor: 'rgba(0, 0, 0, 0.04)', color: 'text.disabled' },
            '& .ignored-row:hover': { backgroundColor: 'rgba(0, 0, 0, 0.06)' },
            '& .highlighted-row': { backgroundColor: 'rgba(255, 193, 7, 0.2)' },
            '& .highlighted-row:hover': { backgroundColor: 'rgba(255, 193, 7, 0.3)' },
          }}
        />
      </Paper>

      {/* 미루기: 1/3/7일 중 선택 */}
      <Menu anchorEl={snoozeAnchorEl} open={Boolean(snoozeAnchorEl)} onClose={closeSnoozeMenu}>
        <MenuItem disabled={snoozing} onClick={() => handleSnooze(1)}>1일 미루기</MenuItem>
        <MenuItem disabled={snoozing} onClick={() => handleSnooze(3)}>3일 미루기</MenuItem>
        <MenuItem disabled={snoozing} onClick={() => handleSnooze(7)}>7일 미루기</MenuItem>
      </Menu>
    </Box>
  );
};

export default ShipmentCandidateListContainer;
